import React from 'react';

/**
 * AssessmentStep3Questions
 *
 * Props:
 * - questions: array of question objects
 * - currentQuestionIndex: number, index of the currently displayed question
 * - answers: object mapping questionId -> answer value
 * - handleAnswerChange: function(questionId, value) to update answers
 */
const AssessmentStep3Questions = ({
  questions,
  currentQuestionIndex,
  answers,
  handleAnswerChange
}) => {
  if (!questions || questions.length === 0) {
    return null;
  }

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Assessment Questions
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Question {currentQuestionIndex + 1} of {questions.length}
        </p>
      </div>

      <div className="mb-8">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          {currentQuestion?.question_text}
        </h3>

        {/* Question input based on type */}
        <div className="mb-6">
          {(currentQuestion?.question_type === 'yes_no' || currentQuestion?.question_type === 'boolean') && (
            <div className="space-y-3">
              {/* Use options from database if available, otherwise default to Yes/No */}
              {(() => {
                let options = ['Yes', 'No']; // Default options
                
                // Parse options from database if available
                if (currentQuestion?.options) {
                  try {
                    const parsedOptions = typeof currentQuestion.options === 'string' 
                      ? JSON.parse(currentQuestion.options) 
                      : currentQuestion.options;
                    if (Array.isArray(parsedOptions) && parsedOptions.length > 0) {
                      options = parsedOptions;
                    }
                  } catch (e) {
                    // If parsing fails, use default Yes/No
                    console.warn('Failed to parse question options, using default Yes/No:', e);
                  }
                }
                
                return options.map((option) => (
                  <label key={option} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name={currentQuestion.id}
                      value={option}
                      checked={answers[currentQuestion.id] === option}
                      onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                      className="mr-3 text-emerald-700 dark:text-emerald-500"
                    />
                    <span className="text-gray-700 dark:text-gray-300">{option}</span>
                  </label>
                ));
              })()}
            </div>
          )}

          {currentQuestion?.question_type === 'numeric' && (
            <input
              type="number"
              value={answers[currentQuestion.id] || ''}
              onChange={(e) =>
                handleAnswerChange(currentQuestion.id, parseFloat(e.target.value) || '')
              }
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
              placeholder="Enter value"
            />
          )}

          {(!currentQuestion?.question_type ||
            currentQuestion?.question_type === 'text') && (
            <input
              type="text"
              value={answers[currentQuestion.id] || ''}
              onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
              placeholder="Enter your answer"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AssessmentStep3Questions;


